import { useCallback, useEffect, useState } from "react";
import api from "api/api";

// Props Validations
import PropTypes from "prop-types";

// antd components
import { Typography } from "antd";

// mui components & icons
import { Box, Button, Checkbox, Tooltip, Dialog, DialogContent, DialogActions } from "@mui/material";
import ControlPointRoundedIcon from "@mui/icons-material/ControlPointRounded";
import InfoIcon from "@mui/icons-material/Info";

// redux
import { useSelector, useDispatch } from "react-redux";
import { loadSelectedProductState, loadSelectedUserProfilesState, loadCriteriaSelectionViewState, loadSelectedCriterionOptionsState, loadSelectedCriteriaState } from "../../store/features/autoDeckReportSlice";
import { showNotification } from "store/features/notificationSlice";

// components
import UserProfileCreateUpdate from "./UserProfileCreateUpdate";
import Loader from "../common/Loader";
import UserProfileDelete from "./UserProfileDelete";

//utils
import GetConsumerFilterOrder from "utils/GetConsumerFilterOrder";
import CriteriaSelectionView from "./CriteriaSelectionView";

function ReportCreationView() {
    const dispatch = useDispatch();
    const [products, setProducts] = useState([]);
    const [userProfiles, setUserProfiles] = useState([]);
    // const [selectedUserProfiles, setSelectedUserProfiles] = useState([]);
    const [filtersResponse, setFiltersResponse] = useState({});
    const [consumerFilters, setConsumerFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // redux states
    const selectedProducts = useSelector((state) => state.autoDeckReportStore.selectedProduct);
    const selectedUserProfiles = useSelector((state) => state.autoDeckReportStore.selectedUserProfiles);
    const criteriaSelectionView = useSelector((state) => state.autoDeckReportStore.criteriaSelectionView);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const studyType = selectedStudy.length > 0 ? [...new Set(selectedStudy.map((ele) => ele.study_type))].join(", ") : null;
    const studyTypeMethod = selectedStudy.length > 0 ? [...new Set(selectedStudy.map((ele) => ele.study_type_method))].join(", ") : null;
    const isDisabled = selectedProducts.length !== 2;

    // dialog states
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isUpdateOpen, setIsUpdateOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isProceedDialogOpen, setIsProceedDialogOpen] = useState(false);

    const disableCreateUserProfile = userProfiles.length >= 5;

    // order
    const customOrder = GetConsumerFilterOrder(productCategoryData?.label?.toLowerCase());

    const handleOpen = () => {
        setIsCreateOpen(true);
    };

    const handleUpdateOpen = () => {
        setIsUpdateOpen(true);
    };

    const handleDeleteOpen = () => {
        setIsDeleteOpen(true);
    };

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const clearCreationView = () => {
        dispatch(loadSelectedCriteriaState("introduction"));
        dispatch(loadSelectedCriterionOptionsState({}));
    }

    const handleToggle = (product) => {
        const updatedSelectedProducts = selectedProducts.includes(product) ? selectedProducts.filter((item) => item !== product) : [...selectedProducts, product];
        dispatch(loadSelectedProductState(updatedSelectedProducts));
        clearCreationView();
        dispatch(loadSelectedUserProfilesState([]));
    };

    const handleUserProfilesToggle = (profile) => {
        const updatedSelectedUserProfiles = selectedUserProfiles.some((item) => item.id === profile.id) ? selectedUserProfiles.filter((item) => item.id !== profile.id) : [...selectedUserProfiles, profile];
        clearCreationView();
        dispatch(loadSelectedUserProfilesState(updatedSelectedUserProfiles));
    };

    const handleCloseDialog = () => {
        setIsProceedDialogOpen(false);
    };

    const handleProceedDialogSubmit = () => {
        // naviagte to criteria selection view
        dispatch(loadCriteriaSelectionViewState(true));
        setIsProceedDialogOpen(false);
    };

    const handleProceed = () => {
        if (selectedUserProfiles.length > 0) {
            // naviagate to criteria selection view
            dispatch(loadCriteriaSelectionViewState(true));
        } else {
            setIsProceedDialogOpen(true);
        }
    };

    useEffect(() => {
        if (selectedStudy.length > 0) {
            const ulProducts = [...(selectedStudy[0]?.products ?? [])].sort((a, b) => a.localeCompare(b));
            const compProducts = [...(selectedStudy[0]?.competitor_products ?? [])].sort((a, b) => a.localeCompare(b));
            setProducts([...ulProducts, ...compProducts]);
        }
    }, [selectedStudy]);

    const buildFilterBvArray = (tempData, _backendVariable) => {
        const array = tempData?.backend_variable_mapping[_backendVariable].map((datum) => {
            return {
                value: datum,
                label: datum,
                checked: false,
            };
        });
        return array;
    };

    const formatConsumerFilters = (data) => {
        const temp = {};
        const sortedVariables = data.backend_variable_display_label.slice().sort((a, b) => {
            const indexA = customOrder.indexOf(a.displayLabel);
            const indexB = customOrder.indexOf(b.displayLabel);

            if (indexA === -1 && indexB === -1) {
                return a.displayLabel.localeCompare(b.displayLabel);
            }

            if (indexA === -1) {
                return 1;
            }

            if (indexB === -1) {
                return -1;
            }

            return indexA - indexB;
        });

        sortedVariables.forEach((element) => {
            const _backendVariable = element.backendVariable;
            if (consumerFilters && consumerFilters[_backendVariable]) {
                temp[_backendVariable] = consumerFilters[_backendVariable];
            } else {
                const array = buildFilterBvArray(data, _backendVariable);
                temp[_backendVariable] = [...array];
            }
        });
        setConsumerFilters({ ...temp });
    };

    const fetchConsumerFilter = useCallback(() => {
        if (selectedProducts.length === 2) {
            const coreDataId = selectedStudy[0]?.id || "";
            const url =
                `${process.env.REACT_APP_SERVER_PATH}/data-mappings/dynamic-consumer-filters/?archive=false&uniq=true&combined=true&variable_category=${encodeURIComponent("Consumer Filter,Study Filter,Consumer Filter related to Product Trial")}&core_data_model=${coreDataId}&products=${encodeURIComponent(selectedProducts.join(","))}` +
                (productCategoryId ? `&product_category=${productCategoryId}` : "") +
                (studyType ? `&study_type=${studyType}` : "") +
                (studyTypeMethod ? `&study_type_method=${studyTypeMethod}` : "");
            setIsLoading(true);
            try {
                api.get(url)
                    .then(async (res) => {
                        formatConsumerFilters(res.data);
                        setFiltersResponse(res.data);
                        setIsLoading(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    }, [dispatch, selectedStudy, productCategoryId, handleError, selectedProducts]);

    useEffect(() => {
        fetchConsumerFilter();
    }, [fetchConsumerFilter]);

    const getUserProfiles = useCallback(() => {
        if (selectedStudy.length === 1) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/user-profile-infos/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            setUserProfiles(response);
                            if (selectedUserProfiles.length > 0) {
                                const updatedSelectedUserProfiles = selectedUserProfiles.map((item) => response.find((data) => data.id === item.id)).filter(Boolean);
                                dispatch(loadSelectedUserProfilesState(updatedSelectedUserProfiles));
                            }
                        })
                        .catch(async (error) => {
                            handleError("error", error);
                        });
                } catch (error) {
                    handleError("error", error);
                }
            }
        }
    }, [selectedStudy, handleError]);

    useEffect(() => {
        getUserProfiles();
    }, [getUserProfiles]);

    return (
        <>
            {criteriaSelectionView ? (
                <CriteriaSelectionView />
            ) : (
                <>
                    {isLoading && <Loader />}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginLeft: "20px",
                            height: "calc(100vh - 300px)",
                            gap: "30px",
                        }}
                    >
                        <div
                            style={{
                                width: "45%",
                                backgroundColor: "#FAF9F9",
                                borderRadius: "2px",
                                boxShadow: " rgba(0, 0, 0, 0.16) 0px 1px 4px",
                                padding: "16px",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "auto",
                            }}
                        >
                            <h1 style={{ color: "#005EEF", textAlign: "center", fontSize: "1.5rem", fontWeight: "bold" }}>Product Selection</h1>
                            <p style={{ fontSize: "12px", textAlign: "center" }}>{`(Study Reference - ${selectedStudy[0]?.study_reference__title})`}</p>
                            <div
                                style={{
                                    marginTop: "20px",
                                }}
                            >
                                {products.map((item) => {
                                    const isDisabled = selectedProducts.length === 2 && !selectedProducts.includes(item);
                                    return (
                                        <Box
                                            key={item}
                                            onClick={() => !isDisabled && handleToggle(item)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: "#FAF9F9",
                                                },
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={selectedProducts.includes(item)}
                                                disabled={isDisabled}
                                            />

                                            <Typography style={isDisabled ? { color: "#ccc", fontSize: "1rem" } : { fontSize: "1rem" }}>{item}</Typography>
                                        </Box>
                                    );
                                })}
                            </div>
                            <p style={{ paddingLeft: "10px", fontSize: "12px", marginTop: "auto" }}>
                                <span style={{ fontWeight: "bold" }}>Note:</span> Please note that two products must be selected for the AutoDeck report
                            </p>
                        </div>
                        <div
                            style={{
                                position: "relative",
                                width: "55%",
                                backgroundColor: isDisabled ? "#E8E7E7" : "#FAF9F9",
                                borderRadius: "2px",
                                boxShadow: " rgba(0, 0, 0, 0.16) 0px 1px 4px",
                                padding: "16px",
                                opacity: isDisabled ? 0.5 : 1,
                                pointerEvents: isDisabled ? "none" : "auto",
                                cursor: isDisabled ? "not-allowed" : "default",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "auto",
                                    height: "100%",
                                }}
                            >
                                <h1 style={{ color: "#005EEF", textAlign: "center", fontSize: "1.5rem", fontWeight: "bold" }}>User Profile Selection</h1>
                                <p style={{ textAlign: "center", fontSize: "0.875rem", marginBottom: "20px" }}>
                                    Choose Existing Profile To Edit Or <br />
                                    Create New Profile
                                </p>
                                <Tooltip
                                    title={disableCreateUserProfile ? "More than 5 user profiles cannot be created" : ""}
                                    placement="top-start"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: "rgb(51, 94, 234,0.1)",
                                                color: "#000",
                                                maxWidth: 300,
                                                boxShadow: 2,
                                                zIndex: 9999,
                                            },
                                        },
                                        arrow: {
                                            sx: {
                                                color: "#F5F5F5",
                                            },
                                        },
                                        popper: {
                                            modifiers: [
                                                {
                                                    name: "offset",
                                                    options: {
                                                        offset: [-120, -11],
                                                    },
                                                },
                                            ],
                                        },
                                    }}
                                >
                                    <span
                                        style={{
                                            alignSelf: "center",
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            className={disableCreateUserProfile ? "custom-btn bold-content" : "custom-btn custom-btn-outlined-2 color-blue bold-content"}
                                            variant="outlined"
                                            startIcon={<ControlPointRoundedIcon />}
                                            onClick={handleOpen}
                                            disabled={disableCreateUserProfile}
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor: "#D4ECFA",
                                                },
                                            }}
                                        >
                                            Create a New User Profile
                                        </Button>
                                    </span>
                                </Tooltip>
                                <div
                                    style={{
                                        marginTop: "20px",
                                    }}
                                >
                                    {userProfiles.map((item) => {
                                        const isDisabled = selectedUserProfiles.length === 3 && !selectedUserProfiles.some((profile) => profile.id === item.id);
                                        const isChecked = selectedUserProfiles.some((profile) => profile.id === item.id);
                                        const isUpdated = item.last_modified && new Date(item.date_created).toISOString().slice(0, 19) !== new Date(item.last_modified).toISOString().slice(0, 19);
                                        return (
                                            <Box
                                                key={item.id}
                                                onClick={() => !isDisabled && handleUserProfilesToggle(item)}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        backgroundColor: "#FAF9F9",
                                                    },
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={isChecked}
                                                    disabled={isDisabled}
                                                />
                                                <Typography style={isDisabled ? { color: "#ccc", fontSize: "0.875rem" } : { fontSize: "0.875rem" }}>{item.title}</Typography>
                                                <Tooltip
                                                    arrow
                                                    placement="right-start"
                                                    title={
                                                        <Box>
                                                            <Typography
                                                                variant="inherit"
                                                                style={{ fontWeight: "bold", fontSize: "8px", lineHeight: 1.2 }}
                                                            >
                                                                Created by :
                                                            </Typography>

                                                            <Typography
                                                                variant="inherit"
                                                                style={{ fontSize: "8px", lineHeight: 1.2 }}
                                                            >
                                                                {item.created_by_email}
                                                            </Typography>

                                                            <Typography
                                                                variant="inherit"
                                                                style={{ fontSize: "7px", lineHeight: 1.2 }}
                                                            >
                                                                on {new Date(item.date_created).toLocaleDateString("en-GB")}
                                                            </Typography>
                                                            {isUpdated && (
                                                                <>
                                                                    <Typography
                                                                        variant="inherit"
                                                                        style={{ fontWeight: "bold", fontSize: "8px", lineHeight: 1.2 }}
                                                                    >
                                                                        Last Updated by :
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="inherit"
                                                                        style={{ fontSize: "8px", lineHeight: 1.2 }}
                                                                    >
                                                                        {item.updated_by_email}
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="inherit"
                                                                        style={{ fontSize: "7px", lineHeight: 1.2 }}
                                                                    >
                                                                        on {new Date(item.last_modified).toLocaleDateString("en-GB")}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Box>
                                                    }
                                                    slotProps={{
                                                        tooltip: {
                                                            sx: {
                                                                backgroundColor: "#DCE5F3",
                                                                color: "#000",
                                                                borderRadius: "8px",
                                                                padding: "4px 6px",
                                                                minWidth: "120px",
                                                                maxWidth: "150px",
                                                            },
                                                        },
                                                        arrow: {
                                                            sx: { color: "#DCE5F3" },
                                                        },
                                                    }}
                                                >
                                                    <InfoIcon
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ color: "#1565F7", fontSize: "10px", ml: "0px", mt: "-12px", cursor: "pointer" }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        );
                                    })}
                                </div>
                                {selectedUserProfiles.length > 0 && (
                                    <>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                paddingTop: "10px",
                                                paddingBottom: "10px",
                                                gap: "20px",
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                className="custom-btn liner-light-blue-background"
                                                variant="contained"
                                                onClick={handleUpdateOpen}
                                            >
                                                Update Profile
                                            </Button>
                                            <Button
                                                size="small"
                                                className="custom-btn liner-light-blue-background"
                                                variant="contained"
                                                onClick={handleDeleteOpen}
                                            >
                                                Delete Profile
                                            </Button>
                                        </div>
                                        {selectedUserProfiles.length === 3 && (
                                            <p style={{ paddingLeft: "11px", fontSize: "12px", marginTop: "10px", marginBottom: "8px" }}>
                                                <span style={{ fontWeight: "bold" }}>Note:</span> A maximum of three User Profiles can be selected at a time.
                                            </p>
                                        )}
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "7px",
                                                    height: "7px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#ccc",
                                                    marginRight: "-1px",
                                                    zIndex: 1,
                                                }}
                                            />

                                            <div
                                                style={{
                                                    height: "1px",
                                                    backgroundColor: "#ccc",
                                                    flex: 1,
                                                }}
                                            />

                                            <div
                                                style={{
                                                    width: "6px",
                                                    height: "6px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#ccc",
                                                    marginLeft: "-1px",
                                                    zIndex: 1,
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <h1 style={{ color: "#005EEF", textAlign: "center", fontSize: "1.25rem", fontWeight: "bold", marginBottom: "16px", marginTop: "16px" }}>Profile Summary</h1>
                                            {selectedUserProfiles.map((profile) => {
                                                return (
                                                    <div
                                                        key={profile.id}
                                                        style={{ paddingLeft: "11px", marginBottom: "10px" }}
                                                    >
                                                        <p style={{ fontSize: "0.875rem", fontWeight: "bold", textDecoration: "underline", marginBottom: "10px" }}>{profile.title} -</p>
                                                        {Object.entries(profile.user_selected_filters).map(([key, value]) => {
                                                            return (
                                                                <div
                                                                    style={{ display: "flex", marginBottom: "0px" }}
                                                                    key={key}
                                                                >
                                                                    <p style={{ fontSize: "0.75rem", fontWeight: "bold", marginRight: "8px" }}>{key}:</p>
                                                                    <p style={{ fontSize: "0.75rem" }}>{value.join(", ")}</p>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                            <UserProfileCreateUpdate
                                isOpen={isCreateOpen}
                                setIsOpen={setIsCreateOpen}
                                type={"create"}
                                filtersResponse={filtersResponse}
                                consumerFilters={consumerFilters}
                                setConsumerFilters={setConsumerFilters}
                                userProfiles={userProfiles}
                                getUserProfiles={getUserProfiles}
                                selectedUserProfiles={selectedUserProfiles}
                            />
                            <UserProfileCreateUpdate
                                isOpen={isUpdateOpen}
                                setIsOpen={setIsUpdateOpen}
                                type={"update"}
                                filtersResponse={filtersResponse}
                                consumerFilters={consumerFilters}
                                setConsumerFilters={setConsumerFilters}
                                userProfiles={userProfiles}
                                getUserProfiles={getUserProfiles}
                                selectedUserProfiles={selectedUserProfiles}
                            />
                            <UserProfileDelete
                                isOpen={isDeleteOpen}
                                setIsOpen={setIsDeleteOpen}
                                selectedUserProfiles={selectedUserProfiles}
                                getUserProfiles={getUserProfiles}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            paddingTop: "20px",
                        }}
                    >
                        <Button
                            size="large"
                            className={isDisabled ? "custom-btn me-4 disabled" : "custom-btn liner-light-blue-background me-4"}
                            variant="contained"
                            onClick={handleProceed}
                            disabled={isDisabled}
                        >
                            Proceed
                        </Button>
                        <Button
                            size="large"
                            className={isDisabled ? "custom-btn me-4 disabled" : "custom-btn custom-btn-outlined-2 color-blue me-4"}
                            variant="outlined"
                            disabled={isDisabled}
                        >
                            Reset
                        </Button>
                    </div>
                    {isProceedDialogOpen && (
                        <Dialog
                            open={isProceedDialogOpen}
                            fullWidth={true}
                            maxWidth="sm"
                        >
                            <DialogContent style={{ textAlign: "center", paddingBottom: "0px" }}>
                                <span>
                                    No User Profile has been selected.
                                    <br /> The overall sample will be considered for report creation.
                                </span>
                                <p style={{ marginTop: "16px" }}>Are you sure you want to proceed?</p>
                            </DialogContent>
                            <DialogActions
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "0px 0px 20px 0px",
                                }}
                            >
                                <Button
                                    size="medium"
                                    className="custom-btn liner-light-blue-background"
                                    variant="contained"
                                    onClick={handleProceedDialogSubmit}
                                >
                                    Proceed
                                </Button>
                                <Button
                                    size="medium"
                                    className="custom-btn custom-btn-outlined-2 color-blue"
                                    variant="outlined"
                                    onClick={handleCloseDialog}
                                >
                                    Cancel
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </>
            )}
        </>
    );
}

// Typechecking props for the ReportCreationView
ReportCreationView.propTypes = {
    setTab: PropTypes.func,
};

export default ReportCreationView;
